import { useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { RapierRigidBody } from '@react-three/rapier'
import { Ball } from './Ball'
import { Ground } from './Ground'
import { Goal } from './Goal'
import { SwipeHandler } from './SwipeHandler'
import { useGame } from '../contexts/GameContext'

export const GameScene: React.FC = () => {
  const ballRef = useRef<RapierRigidBody>(null)
  const { setBallPosition, endGame, gameState, setCrossbarHit, setSwipeStart, setSwipeEnd, resetScore } = useGame()
  const lastShotRef = useRef(false)
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastShotWasHit = useRef(false) // <--- добавлено

  // Debug information
  useEffect(() => {
    console.log('GameScene mounted')
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    console.log('Is mobile:', isMobile)
    console.log('WebGL context:', document.createElement('canvas').getContext('webgl'))
  }, [])

  useFrame(() => {
    if (ballRef.current) {
      const position = ballRef.current.translation()
      setBallPosition([position.x, position.y, position.z])

      // Only run shot/ground/end logic if game is started
      if (!gameState.gameStarted) return;

      // Detect if ball is moving (shot taken)
      const velocity = ballRef.current.linvel()
      const isMoving = Math.abs(velocity.x) > 0.1 || Math.abs(velocity.y) > 0.1 || Math.abs(velocity.z) > 0.1

      // If shot just started, reset crossbarHit and set up reset timer
      if (isMoving && !lastShotRef.current) {
        setCrossbarHit(false)
        lastShotRef.current = true
        lastShotWasHit.current = false // сбрасываем перед новым ударом
        // Clear any previous timeout
        if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current)
        // Set up a 3-second reset after shot
        resetTimeoutRef.current = setTimeout(() => {
          if (ballRef.current) {
            ballRef.current.setTranslation({ x: 0, y: 0.15, z: -11 }, true)
            ballRef.current.setLinvel({ x: 0, y: 0, z: 0 }, true)
            ballRef.current.setAngvel({ x: 0, y: 0, z: 0 }, true)
            setSwipeStart(null)
            setSwipeEnd(null)
            setCrossbarHit(false)
            lastShotRef.current = false
            // Используем lastShotWasHit для проверки
            if (!lastShotWasHit.current) {
              endGame()
              resetScore()
            }
            // If hit, do nothing: score is incremented in Goal/onCollisionEnter
          }
        }, 2000)
      }

      // If ball touches crossbar, increment score (handled by Goal/onCollisionEnter)
      // If ball is stopped and above ground, allow new shot
      if (!isMoving && position.y > 0.16) {
        lastShotRef.current = false
      }
    }
  })

  // Reset ball position and shot state when game is reset (Play Again)
  useEffect(() => {
    if (ballRef.current && !gameState.isGameOver) {
      ballRef.current.setTranslation({ x: 0, y: 0.2, z: -11 }, true)
      ballRef.current.setLinvel({ x: 0, y: 0, z: 0 }, true)
      ballRef.current.setAngvel({ x: 0, y: 0, z: 0 }, true)
      lastShotRef.current = false // Prevent immediate endGame after reset
      setSwipeStart(null)
      setSwipeEnd(null)
    }
    // Clear timeout on game reset
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current)
  }, [gameState.isGameOver])

  return (
    <>
      {/* Simple lighting for mobile */}
      <ambientLight intensity={0.6} />
      <directionalLight 
        position={[5, 10, -5]} 
        intensity={0.8} 
        castShadow={false} // Disable shadows for performance
      />
      
      {/* Ball */}
      <Ball ref={ballRef} />
      
      {/* Ground */}
      <Ground />
      
      {/* Goal */}
      <Goal lastShotWasHit={lastShotWasHit} />
      
      {/* Swipe handler */}
      <SwipeHandler ballRef={ballRef} />
    </>
  )
} 